import React, { useState, useEffect, useRef, useCallback } from "react";
import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
import { Film, Upload as UploadIcon, RefreshCw, X, Library, Play, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * MuxVideoPicker — a drop-in replacement for "paste a Mux playback ID" inputs.
 *
 * Props:
 *   value         — current Mux playback ID (string | null)
 *   onChange(id)  — called with the new playback ID (or null to clear)
 *   testidPrefix  — prefix for data-testid attrs (e.g. "studio-scene-mux-0")
 *   compact       — boolean — render a smaller trigger row (used inside scene rows)
 *   label         — optional override label shown when empty
 */
export default function MuxVideoPicker({ value, onChange, testidPrefix = "mux-picker", compact = false, label }) {
  const [open, setOpen] = useState(false);

  if (value) {
    // Already linked — show preview + actions
    return (
      <div className="space-y-2" data-testid={`${testidPrefix}-set`}>
        <div className="rounded overflow-hidden border" style={{ borderColor: "var(--gio-border)" }}>
          <MuxPlayer playbackId={value} accentColor="#14B8A6" style={{ width: "100%", aspectRatio: "16/9" }} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono-ui truncate" style={{ color: "var(--gio-muted-2)" }}>
            ID: <span style={{ color: "var(--gio-text)" }}>{value}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => setOpen(true)}
              data-testid={`${testidPrefix}-replace`}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border transition hover:border-[color:var(--gio-secondary)]"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted)" }}>
              <RefreshCw size={11} /> Replace
            </button>
            <button type="button" onClick={() => onChange(null)}
              data-testid={`${testidPrefix}-clear`}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border transition hover:border-[color:var(--gio-primary)]"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted)" }}>
              <Trash2 size={11} /> Clear
            </button>
          </div>
        </div>
        {open && <PickerModal onClose={() => setOpen(false)} onPick={(id) => { onChange(id); setOpen(false); }} testidPrefix={testidPrefix} />}
      </div>
    );
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        data-testid={`${testidPrefix}-open`}
        className={`w-full inline-flex items-center justify-center gap-2 border-2 border-dashed rounded transition hover:border-[color:var(--gio-secondary)] hover:bg-[var(--gio-primary-soft)] ${compact ? "py-2.5 text-[11px]" : "py-5 text-[13px]"}`}
        style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted)" }}>
        <Film size={compact ? 12 : 16} />
        <span className="font-bold uppercase tracking-wider">{label || (compact ? "Add video" : "Add Mux video — library or upload")}</span>
      </button>
      {open && <PickerModal onClose={() => setOpen(false)} onPick={(id) => { onChange(id); setOpen(false); }} testidPrefix={testidPrefix} />}
    </>
  );
}

function PickerModal({ onClose, onPick, testidPrefix }) {
  const [mode, setMode] = useState("choose"); // choose | library | upload | processing
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [uploadInfo, setUploadInfo] = useState(null); // {upload_id, upload_url}
  const [uploadStatus, setUploadStatus] = useState(null); // {status, playback_id, ...}
  const pollRef = useRef(null);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/mux/assets?limit=50");
      setAssets(r.data.assets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Mux library");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (mode === "library" && assets.length === 0) loadAssets();
  }, [mode, assets.length, loadAssets]);

  const startUpload = async () => {
    try {
      const r = await api.post("/api/mux/uploads/direct", {});
      setUploadInfo(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start upload");
    }
  };

  // Poll upload status when in processing mode
  const pollOnce = useCallback(async (uploadId) => {
    try {
      const r = await api.get(`/api/mux/uploads/${uploadId}`);
      setUploadStatus(r.data);
      if (r.data.playback_id && r.data.status === "ready") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.success("Video ready — linking…");
        onPick(r.data.playback_id);
      }
      if (r.data.status === "errored") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.error("Mux processing failed");
      }
    } catch {/* keep polling */}
  }, [onPick]);

  useEffect(() => {
    if (mode !== "processing" || !uploadInfo?.upload_id) return;
    pollOnce(uploadInfo.upload_id); // immediate
    pollRef.current = setInterval(() => pollOnce(uploadInfo.upload_id), 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [mode, uploadInfo?.upload_id, pollOnce]);

  const filteredAssets = filter
    ? assets.filter(a => (a.id || "").toLowerCase().includes(filter.toLowerCase()) || (a.playback_id || "").toLowerCase().includes(filter.toLowerCase()))
    : assets;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid={`${testidPrefix}-modal`}
      onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl rounded-xl overflow-hidden flex flex-col max-h-[90vh]"
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div className="overline" style={{ color: "var(--gio-secondary)" }}>
            {mode === "library" ? "MUX LIBRARY" : mode === "upload" ? "UPLOAD VIDEO" : mode === "processing" ? "PROCESSING" : "ADD MUX VIDEO"}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid={`${testidPrefix}-close`}>
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {mode === "choose" && (
            <div className="grid grid-cols-2 gap-3" data-testid={`${testidPrefix}-choose`}>
              <button onClick={() => setMode("library")} type="button"
                data-testid={`${testidPrefix}-mode-library`}
                className="p-5 rounded-lg border-2 text-left transition hover:-translate-y-0.5 hover:border-[color:var(--gio-secondary)]"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
                <Library size={22} style={{ color: "var(--gio-secondary)" }} />
                <div className="font-heading text-[16px] mt-2" style={{ color: "var(--gio-text)" }}>From Mux Library</div>
                <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                  Pick a video already uploaded to your Mux account
                </div>
              </button>
              <button onClick={() => { setMode("upload"); startUpload(); }} type="button"
                data-testid={`${testidPrefix}-mode-upload`}
                className="p-5 rounded-lg border-2 text-left transition hover:-translate-y-0.5 hover:border-[color:var(--gio-primary)]"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
                <UploadIcon size={22} style={{ color: "var(--gio-primary)" }} />
                <div className="font-heading text-[16px] mt-2" style={{ color: "var(--gio-text)" }}>Upload New</div>
                <div className="text-[11px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                  Upload from this device — auto-transcoded by Mux
                </div>
              </button>
            </div>
          )}

          {mode === "library" && (
            <div data-testid={`${testidPrefix}-library`}>
              <div className="flex items-center gap-2 mb-3">
                <input value={filter} onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter by asset ID…"
                  data-testid={`${testidPrefix}-filter`}
                  className="flex-1 bg-black border px-3 py-2 text-[12px] font-mono-ui rounded outline-none"
                  style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }} />
                <button onClick={loadAssets} disabled={loading}
                  data-testid={`${testidPrefix}-refresh`}
                  className="btn-ghost text-[11px] inline-flex items-center gap-1">
                  <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
                <button onClick={() => setMode("choose")} className="text-[11px] underline text-zinc-500 hover:text-white">← back</button>
              </div>
              {loading && <div className="text-center py-8 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>Loading…</div>}
              {!loading && filteredAssets.length === 0 && (
                <div className="text-center py-8 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
                  No assets found. Upload from this device or via Mux dashboard.
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredAssets.map(a => (
                  <button key={a.id} onClick={() => a.playback_id && onPick(a.playback_id)} type="button"
                    disabled={!a.playback_id}
                    data-testid={`${testidPrefix}-asset-${a.id}`}
                    className="group relative rounded-lg overflow-hidden border text-left transition hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
                    {a.playback_id ? (
                      <img src={`https://image.mux.com/${a.playback_id}/thumbnail.jpg?width=320&height=180&fit_mode=smartcrop&time=2`}
                        alt="" className="w-full aspect-video object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-video flex items-center justify-center" style={{ background: "var(--gio-surface)" }}>
                        <Film size={22} style={{ color: "var(--gio-muted-3)" }} />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      style={{ background: "rgba(0,0,0,0.55)" }}>
                      <Play size={26} style={{ color: "#fff" }} />
                    </div>
                    <div className="p-2">
                      <div className="text-[10px] font-mono-ui truncate" style={{ color: "var(--gio-text)" }}>{a.id}</div>
                      <div className="flex items-center gap-1 text-[10px] mt-0.5" style={{ color: "var(--gio-muted-2)" }}>
                        <span className="capitalize">{a.status}</span>
                        {a.duration && <><span>·</span><span>{Math.round(a.duration)}s</span></>}
                        {!a.playback_id && <><span>·</span><span style={{ color: "var(--gio-primary)" }}>no public playback</span></>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "upload" && (
            <div data-testid={`${testidPrefix}-upload`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[12px]" style={{ color: "var(--gio-muted-2)" }}>
                  Drop a video file below — it'll upload directly to Mux, transcode, and link automatically.
                </div>
                <button onClick={() => setMode("choose")} className="text-[11px] underline text-zinc-500 hover:text-white">← back</button>
              </div>
              {uploadInfo ? (
                <MuxUploader
                  endpoint={uploadInfo.upload_url}
                  onSuccess={() => { toast.success("Uploaded — Mux is processing…"); setMode("processing"); }}
                  onUploadError={(err) => toast.error(`Upload failed: ${err?.detail?.message || err?.message || "unknown"}`)}
                  style={{ "--button-background-color": "#14B8A6", "--button-color": "#fff", display: "block" }}
                />
              ) : (
                <div className="text-center py-8 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
                  Preparing upload…
                </div>
              )}
            </div>
          )}

          {mode === "processing" && (
            <div className="py-8 text-center" data-testid={`${testidPrefix}-processing`}>
              <Film size={42} className="mx-auto mb-3 animate-pulse" style={{ color: "var(--gio-secondary)" }} />
              <div className="font-heading text-lg" style={{ color: "var(--gio-text)" }}>Mux is processing your video</div>
              <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                Status: <span className="capitalize">{uploadStatus?.status || "waiting"}</span>
                {uploadStatus?.duration && ` · ${Math.round(uploadStatus.duration)}s`}
              </div>
              <div className="text-[11px] mt-2" style={{ color: "var(--gio-muted-3)" }}>
                Auto-checking every 5 seconds. Usually 1–3 minutes.
              </div>
              {uploadStatus?.playback_id && (
                <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full"
                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
                  <Check size={12} /> Ready — linking…
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
